// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import getColor from "@insite/mobius/utilities/getColor";
import { mobileCartLineContainerStyles } from "@keller/App/Components/Cart/CartContainerLayout";
import cartLineSharedStyles from "@keller/App/SharedStyle/Cart/CartLineSharedStyles";
import { orderLinesListStyles } from "@keller/Overrides/Components/OrderLinesList";
import { constants } from "@keller/XDL/constants";
import { color, hideElementOnPrint, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import { css } from "styled-components";

orderLinesListStyles.gridContainer = {
    css: css`
        display: block;
        width: 100%;
        &:after {
            content: "";
            clear: both;
            display: table;
        }

        @media print {
            border-top: 1px solid ${getColor("common.ui5")};
            padding-top: 12px;
        }
    `,
};
orderLinesListStyles.titleGridItem = {
    ...cartLineSharedStyles.header?.wrapper,
};
orderLinesListStyles.titleText = {
    ...mergeWithCss(cartLineSharedStyles.header?.title, {
        css: css`
            @media print {
                position: relative;
                display: inline-block;
                color: transparent;

                &::before {
                    content: "Products";
                    color: ${color("text.main")};
                    position: absolute;
                    left: 0;
                    top: 0;
                }
            }
        `
    })
};
orderLinesListStyles.productInfoSectionOptionsGridItem = {
    css: css`
        display: flex;
        flex-direction: column;
        margin-top: 5px;
        margin-bottom: 10px;
        & .bold {
            font-weight: bold;
            margin-right: 5px;
        }
    `,
};
orderLinesListStyles.vatLabelText = {
    size: 12,
};
orderLinesListStyles.secondaryVatLabelText = {
    size: 12,
};
orderLinesListStyles.productInfoTotalsGridContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    `,
};
orderLinesListStyles.productDescriptionStyles = {
    ...cartLineSharedStyles.productDescription,
};
orderLinesListStyles.productBrandStyles = {
    ...cartLineSharedStyles.productBrand,
};
orderLinesListStyles.productPartNumbers = {
    ...cartLineSharedStyles.productPartNumbers,

};
orderLinesListStyles.productImageLink = {
    ...cartLineSharedStyles.productImage?.linkWrappingImage,
};
orderLinesListStyles.orderLineCardImage = {
    css: css`
        ${cartLineSharedStyles.productImage?.img?.css}
    `,
};
orderLinesListStyles.productInfoSubtotalHeadingAndText = {
    ...cartLineSharedStyles.extendedUnitNetPriceText,
};
orderLinesListStyles.subtotalWithoutVatHeadingAndText = {
    ...cartLineSharedStyles.extendedUnitNetPriceText,
};
orderLinesListStyles.priceHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.priceHeadingAndText, {
        extendedStyles: {
            heading: {
                css: css`
                    @media print {
                        position: relative;
                        display: inline-block;
                        color: transparent;
                        width: 120px;


                        &::before {
                            content: "${translate("Price/Unit")}";
                            color: ${color("text.main")};
                            position: absolute;
                            left: 0;
                            top: 0;
                        }
                    }
                `
            },
        }

    })
};
orderLinesListStyles.productQtyHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.quantityHeadingAndText, {
        extendedStyles: {
            wrapper: {
                css: css`
                    margin-bottom: 10px;
                `,
            },
            heading: {
                css: css`
                    display: block;
                `,
            },
        },
    }),
};
orderLinesListStyles.readOnlyNotesText = {
    extendedStyles: {
        wrapper: {
            css: css`
                display: flex;
                margin-bottom: 8px;

                @media print {
                    display: none;
                }
            `,
        },
        heading: {
            css: css`
                text-transform: capitalize;
                @media print {
                    min-width: 61px;
                }
            `,
        },
    },
};
orderLinesListStyles.cartLineContainerStyles = {
    peripheralActionsLeftOnPrint: {
        ...mergeWithCss(mobileCartLineContainerStyles.peripheralActionsLeftOnPrint, {
            css: css`
                @media print {
                    &&&& {
                        &,
                        & > span {
                            line-height: 1.5 !important;
                        }
                    }
                }
            `,
        }),
    },
};
orderLinesListStyles.notesTextWrapperOnPrint = {
    css: css`
        display: none;

        @media print {
            display: flex;
        }
    `,
};
orderLinesListStyles.readOnlyNotesTextOnPrint = {
    extendedStyles: {
        heading: {
            css: css`
                @media print {
                    text-transform: capitalize;
                    min-width: 61px;
                }
            `,
        },
    },
};

orderLinesListStyles.addToListLink = {
    ...cartLineSharedStyles.addToListLink,
};
orderLinesListStyles.promotionsContainer = {
    ...cartLineSharedStyles.promotionsContainer,
};
orderLinesListStyles.promotionNameLabel = {
    ...cartLineSharedStyles.promotionNameLabel,
};
orderLinesListStyles.orderLineInfoPromotionText = {
    ...cartLineSharedStyles.promotionNameText,
};

orderLinesListStyles.qtyOrderedTextField = {
    ...mergeWithCss(cartLineSharedStyles.quantityTextField, {
        labelProps: {
            variant: "legend",
            size: 12,
            weight: "normal",
            css: css`
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    display: inline-block;
                }
                @media print {
                    position: relative;
                    display: inline-block;
                    color: transparent;

                    &::before {
                        content: "${translate("QTY Ordered")}";
                        color: ${color("text.main")};
                        position: absolute;
                        left: 0;
                        top: 0;
                    }
                }
            `,
        },
    }),
};

orderLinesListStyles.qtyShippedTextField = {
    ...mergeWithCss(cartLineSharedStyles.quantityTextField, {
        cssOverrides: {
            formField: css`
                ${hideElementOnPrint}
            `,
        },
        labelProps: {
            variant: "legend",
            size: 12,
            weight: "normal",
            css: css`
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    display: inline-block;
                }
            `,
        },
    }),
}
